import { Button, Checkbox, ContentLayout, FormField, Header, Input, SpaceBetween } from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { fetchItemsWithFilter, Filter, updateItem } from '../api/generic';
import { useAuthContext } from '../contexts/AuthContext';
import { Department, Tenant as TenantModel } from '../models/Tenant';

const TenantAdmin = () => {
  const { tenant } = useAuthContext();
  const [tenantObject, setTenantObject] = useState<TenantModel | null>(null);
  const [editedTenant, setEditedTenant] = useState<TenantModel | null>(null);
  const [saving, setSaving] = useState(false);
  const [newDepartment, setNewDepartment] = useState<Department | null>(null);

  useEffect(() => {
    const fetchTenant = async () => {
      if (tenant) {
        const filters: Filter<TenantModel>[] = [{ field: 'name', value: tenant, operator: '==' }];
        const result = await fetchItemsWithFilter<TenantModel>('tenant', filters);
        if (result.length > 0) {
          setTenantObject(result[0]);
          setEditedTenant(result[0]);
        }
      }
    };
    fetchTenant();
  }, [tenant]);

  const handleChange = (field: keyof TenantModel, value: any) => {
    setEditedTenant((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleAddDepartment = () => {
    if (!newDepartment) return;
    if (newDepartment.name?.trim() === '') return;
    if (newDepartment.displayName?.trim() === '') return;
    if (editedTenant?.departments?.some((d) => d.name === newDepartment.name)) return;
    if (editedTenant?.departments?.some((d) => d.displayName === newDepartment.displayName)) return;

    setEditedTenant((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        departments: [...(prev.departments ?? []), newDepartment],
      };
    });

    setNewDepartment(null);
  };

  const handleRemoveDepartment = (name: string) => {
    setEditedTenant((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        departments: (prev.departments ?? []).filter((d) => d.name !== name),
      };
    });
  };

  const handleRepairDepartments = () => {
    setEditedTenant((prev) => {
      if (!prev) return null;
      const departments = prev.departments ?? [];
      // If department is string, convert to object

      return {
        ...prev,
        departments: departments.map((d) => {
          if (typeof d === 'string') {
            return {
              name: d,
              displayName: d,
            };
          }
          return { ...d, name: d.name ?? '', displayName: d.displayName ?? '' };
        }),
      };
    });
  };

  const handleSave = async () => {
    if (!editedTenant || !editedTenant.id) return;
    setSaving(true);
    await updateItem('tenant', editedTenant.id, editedTenant);
    setTenantObject(editedTenant);
    setSaving(false);
  };

  return (
    <ContentLayout header={<Header variant="h2">Tenant Information</Header>}>
      {editedTenant && (
        <SpaceBetween size="m">
          <FormField label="Tenant ID">
            <Input value={editedTenant.id} readOnly />
          </FormField>
          <FormField label="Tenant Name">
            <Input value={editedTenant.name} readOnly />
          </FormField>
          <FormField label="Tier">
            <Input value={editedTenant.tier?.toString() ?? ''} readOnly />
          </FormField>
          <FormField label="HydroPi ID">
            <Input value={editedTenant.hydroPi ?? ''} readOnly />
          </FormField>

          <FormField label="Departments">
            <SpaceBetween size="xs">
              {(editedTenant.departments ?? []).map((dep: Department) => (
                <SpaceBetween key={dep.name} direction="horizontal" size="xs">
                  {typeof dep === 'string' ? (
                    <Input value={dep} readOnly />
                  ) : (
                    <>
                      <Input value={dep.name ?? ''} readOnly />
                      <Input value={dep.displayName ?? ''} readOnly />
                      <Button iconName="close" onClick={() => handleRemoveDepartment(dep.name ?? '')} />
                    </>
                  )}
                </SpaceBetween>
              ))}
              <SpaceBetween size="xs" direction="horizontal">
                <Input
                  placeholder="Full name"
                  value={newDepartment?.name ?? ''}
                  onChange={({ detail }) => {
                    setNewDepartment((prev) => (prev ? { ...prev, name: detail.value } : { name: detail.value }));
                  }}
                />
                <Input
                  placeholder="Display name"
                  value={newDepartment?.displayName ?? ''}
                  onChange={({ detail }) =>
                    setNewDepartment((prev) =>
                      prev ? { ...prev, displayName: detail.value } : { displayName: detail.value },
                    )
                  }
                />
                <Button onClick={handleAddDepartment}>Add</Button>
              </SpaceBetween>
              {editedTenant.departments &&
                editedTenant.departments.length > 0 &&
                editedTenant.departments.some((d) => !d.name || !d.displayName) && (
                  <Button onClick={handleRepairDepartments}>Repair Departments</Button>
                )}
            </SpaceBetween>
          </FormField>

          <FormField label="Display Name">
            <Input
              value={editedTenant.displayName}
              onChange={({ detail }) => handleChange('displayName', detail.value)}
            />
          </FormField>
          <FormField label="Default Currency">
            <Input
              value={editedTenant.currency ?? ''}
              onChange={({ detail }) => handleChange('currency', detail.value)}
            />
            <Checkbox
              checked={editedTenant.currencyPosition === 'after'}
              onChange={({ detail }) => handleChange('currencyPosition', detail.checked ? 'after' : 'before')}
            >
              Currency Symbol After Amount
            </Checkbox>
          </FormField>
          <FormField label="Company Name">
            <Input
              value={editedTenant.companyName ?? ''}
              onChange={({ detail }) => handleChange('companyName', detail.value)}
            />
          </FormField>
          <FormField label="Company Phone">
            <Input
              value={editedTenant.companyPhone ?? ''}
              onChange={({ detail }) => handleChange('companyPhone', detail.value)}
            />
          </FormField>
          <FormField label="Company Address">
            <Input
              value={editedTenant.companyAddress ?? ''}
              onChange={({ detail }) => handleChange('companyAddress', detail.value)}
            />
          </FormField>
          <FormField label="Zip Code">
            <Input
              value={editedTenant.companyZipCode ?? ''}
              onChange={({ detail }) => handleChange('companyZipCode', detail.value)}
            />
          </FormField>
          <FormField label="City">
            <Input
              value={editedTenant.companyCity ?? ''}
              onChange={({ detail }) => handleChange('companyCity', detail.value)}
            />
          </FormField>
          <FormField label="Country">
            <Input
              value={editedTenant.companyCountry ?? ''}
              onChange={({ detail }) => handleChange('companyCountry', detail.value)}
            />
          </FormField>
          <FormField label="VAT Number">
            <Input
              value={editedTenant.vatNumber ?? ''}
              onChange={({ detail }) => handleChange('vatNumber', detail.value)}
            />
          </FormField>
          <FormField label="KvK Number">
            <Input
              value={editedTenant.kvkNumber ?? ''}
              onChange={({ detail }) => handleChange('kvkNumber', detail.value)}
            />
          </FormField>
          <FormField label="IBAN">
            <Input value={editedTenant.iban ?? ''} onChange={({ detail }) => handleChange('iban', detail.value)} />
          </FormField>
          <FormField label="Bank Name">
            <Input
              value={editedTenant.bankName ?? ''}
              onChange={({ detail }) => handleChange('bankName', detail.value)}
            />
          </FormField>
          <FormField label="Email">
            <Input value={editedTenant.email ?? ''} onChange={({ detail }) => handleChange('email', detail.value)} />
          </FormField>
          <FormField label="Default Invoice Notes">
            <Input
              value={editedTenant.invoiceNotes ?? ''}
              onChange={({ detail }) => handleChange('invoiceNotes', detail.value)}
            />
          </FormField>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={saving || editedTenant === tenantObject}
          >
            Save Changes
          </Button>
        </SpaceBetween>
      )}
    </ContentLayout>
  );
};

export default TenantAdmin;
